// @givmo/app-admin — Company DIRECT-GIVING create flow (EP-WEB1). ★ MONEY-MOVING ENTRY ★
//
// A company gives its OWN money to a charity / ETF / fundraiser, independent of matching (D-EP-2/3).
// This page collects the gift (pick target → amount → company funding source) and hands it to
// <DirectGiveDialog>, which owns the confirm-before-charge + all charge-safety guards.
//
// DARK: the whole surface is gated by `isCompanyDirectGivingEnabled()` (VITE_COMPANY_DIRECT_GIVING_
// ENABLED, default off). The `/give` route redirects when off; this page ALSO redirects (defense in
// depth) so the flow is unreachable until the switch flips in lockstep with the backend funding flag.
//
// PERMISSION (D-EP-3): NO Owner-only gate + NO cap. Any admin holding `company:match_donation`
// (Members AND Owners) can give to full wallet/card capacity — so, unlike the dashboard approve
// surface, this page reads NO role and applies NO ceiling. The backend authorizes every charge.
//
// COMPOSE PLUS LEDGER (EP-WEB14, EP-DES1 §2): below the create flow sits the company's own giving
// HISTORY — the read-only "Company donations" table that used to be a dashboard section. Its rows come
// from the dashboard read rather than a dedicated endpoint, so this page fetches
// `companiesGetCompanyDashboard` itself. That is the SAME query key the dashboard uses, so it is a
// cache hit whenever the dashboard was visited this session and one extra GET otherwise — the trade-off
// `app-shell.tsx` already accepts for the approvals badge. It also means the post-charge
// `invalidateMoneyReads` below already refreshes the history: a confirmed gift lands in the table with
// no extra wiring.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  charitiesGetCharitiesOptions,
  etfsGetEtfsOptions,
  fundraisersGetFundraisersOptions,
  fundraisersGetFundraiserOptions,
  companiesGetCompanyCardsV2Options,
  companiesGetCompanyDashboardOptions,
  companiesGetCompanyOptions,
  companiesGetCompanyWalletOptions,
  companiesGetCompanyWalletQueryKey,
  companiesGetCompanyDashboardQueryKey,
} from '@givmo/api-client/query';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Icon,
  PageHeader,
  SearchInput,
  Select,
  Skeleton,
  Tabs,
  TextField,
  testIdProp,
  toast,
  type SelectOption,
} from '@givmo/design-system';
import { HeartHandshake, Wallet as WalletIcon, HeartOff } from 'lucide-react';
import { isCompanyDirectGivingEnabled } from '../../lib/env';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { cardLabel, extractCards } from '../settings/payment-methods-decode';
import {
  extractCharityTargets,
  extractEtfTargets,
  extractFundraiserTargets,
  formatWholeDollarUsd,
  fundingDisplayLabel,
  fundingSummaryLabel,
  parseWholeDollarAmount,
  toWalletFunds,
  walletCoversAmount,
  type Funding,
  type GiveTarget,
  type TargetType,
} from './give-decode';
import { createGiveSeries } from './give-idempotency';
import { DirectGiveDialog } from './direct-give-dialog';
import { GiveHistoryTable } from './give-history-table';
import { toCompanyGaveRows } from '../dashboard/dashboard-decode';

const WALLET_VALUE = 'wallet' as const;

const TARGET_TABS: readonly { type: TargetType; label: string }[] = [
  { type: 'charity', label: 'Charities' },
  { type: 'etf', label: 'Funds' },
  { type: 'fundraiser', label: 'Fundraisers' },
];

export function GivePage({
  preselectFundraiserId = null,
}: { preselectFundraiserId?: string | null } = {}) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const enabled = isCompanyDirectGivingEnabled();

  // Defense in depth: even though the /give route redirects when the switch is off, bounce here too so
  // the flow can never paint while dark (e.g. if the route gate ever regresses).
  useEffect(() => {
    if (!enabled) void navigate({ to: paths.dashboard });
  }, [enabled, navigate, paths.dashboard]);
  if (!enabled) return null;

  return <GiveFlow preselectFundraiserId={preselectFundraiserId} />;
}

function GiveFlow({ preselectFundraiserId }: { preselectFundraiserId: string | null }) {
  const queryClient = useQueryClient();

  // ── Target selection ──
  const [targetType, setTargetType] = useState<TargetType>('charity');
  const [search, setSearch] = useState('');
  const [selectedTarget, setSelectedTarget] = useState<GiveTarget | null>(null);

  // EP-WEB3 seed-CTA preselect: if the route carried `?fundraiserId=`, resolve that fundraiser and
  // preselect it as the target exactly ONCE (never overriding a subsequent user change). Absent → the
  // ordinary interactive target pick.
  const preselect = useQuery({
    ...fundraisersGetFundraiserOptions({ path: { fundraiser_id: preselectFundraiserId ?? '' } }),
    enabled: preselectFundraiserId !== null,
  });
  const preselectApplied = useRef(false);
  useEffect(() => {
    if (preselectApplied.current || preselectFundraiserId === null) return;
    const f = preselect.data;
    if (!f || typeof f.id !== 'string') return;
    preselectApplied.current = true;
    setTargetType('fundraiser');
    setSelectedTarget({
      type: 'fundraiser',
      id: f.id,
      name: typeof f.name === 'string' && f.name.length > 0 ? f.name : 'Fundraiser',
      logoUrl: typeof f.image_url === 'string' ? f.image_url : null,
      subtitle: 'Fundraiser',
    });
  }, [preselectFundraiserId, preselect.data]);

  const query = { q: search.trim().length > 0 ? search.trim() : null, 'page[limit]': 20 } as const;
  // Only the ACTIVE tab's search runs (enabled gate) — no three concurrent target searches.
  const charities = useQuery({
    ...charitiesGetCharitiesOptions({ query }),
    enabled: targetType === 'charity',
  });
  const etfs = useQuery({ ...etfsGetEtfsOptions({ query }), enabled: targetType === 'etf' });
  const fundraisers = useQuery({
    ...fundraisersGetFundraisersOptions({ query }),
    enabled: targetType === 'fundraiser',
  });

  const active = targetType === 'charity' ? charities : targetType === 'etf' ? etfs : fundraisers;
  const results: GiveTarget[] =
    targetType === 'charity'
      ? extractCharityTargets(charities.data)
      : targetType === 'etf'
        ? extractEtfTargets(etfs.data)
        : extractFundraiserTargets(fundraisers.data);

  // ── Amount ──
  const [amountRaw, setAmountRaw] = useState('');
  const amount = parseWholeDollarAmount(amountRaw);
  const amountTouched = amountRaw.trim().length > 0;
  const amountError = amountTouched && amount === null;

  // ── Funding (saved company cards + company wallet) ──
  const cardsQuery = useQuery(companiesGetCompanyCardsV2Options());
  const cards = useMemo(() => extractCards(cardsQuery.data), [cardsQuery.data]);
  const walletQuery = useQuery(companiesGetCompanyWalletOptions());
  const wallet = useMemo(() => toWalletFunds(walletQuery.data), [walletQuery.data]);

  const [fundingValue, setFundingValue] = useState<string | null>(null);
  // Seed the funding default once the cards/wallet resolve: the server-default card, else the first
  // card, else the wallet. Only seeds while nothing is selected (never overrides a user choice).
  useEffect(() => {
    if (fundingValue !== null) return;
    const preferred = cards.find((c) => c.isDefault) ?? cards[0];
    if (preferred) setFundingValue(preferred.id);
    else if (walletQuery.isSuccess) setFundingValue(WALLET_VALUE);
  }, [fundingValue, cards, walletQuery.isSuccess]);

  // ── Giving history (EP-WEB14) — the company's own DIRECT gifts, off the dashboard read. ──
  const history = useQuery(companiesGetCompanyDashboardOptions());
  const historyRows = useMemo(() => toCompanyGaveRows(history.data), [history.data]);

  // ── Review → charge dialog ──
  const [confirmOpen, setConfirmOpen] = useState(false);

  // If the selected card VANISHES from a cards refetch (deleted in another tab), clear the selection
  // so the seed effect re-picks a valid default — never leave a phantom card selected. KEY-SAFE:
  // gated on the confirm dialog being CLOSED — re-scoping the funding under an open (possibly
  // maybe-charged) confirm would change the gift signature mid-series.
  useEffect(() => {
    if (confirmOpen) return;
    if (fundingValue === null || fundingValue === WALLET_VALUE) return;
    if (cardsQuery.isSuccess && !cards.some((c) => c.id === fundingValue)) {
      setFundingValue(null);
    }
  }, [confirmOpen, fundingValue, cards, cardsQuery.isSuccess]);

  const fundingOptions: SelectOption[] = useMemo(
    () => [
      ...cards.map((c) => ({ value: c.id, label: cardLabel(c) })),
      { value: WALLET_VALUE, label: `Company wallet (${wallet.display})` },
    ],
    [cards, wallet.display],
  );

  const funding: Funding | null = useMemo(() => {
    if (fundingValue === null) return null;
    return fundingValue === WALLET_VALUE
      ? { kind: 'wallet' }
      : { kind: 'card', cardId: fundingValue };
  }, [fundingValue]);

  // Tolerant of a vanished card (fallback label, never a render throw) — see fundingDisplayLabel.
  const fundingLabel = fundingDisplayLabel(funding, cards, wallet.display);
  // The confirm dialog's summary names the company as the donor of record (D-CAM-41) — the same
  // `GET /admin/company` read the dashboard and settings share, so usually a cache hit.
  const company = useQuery(companiesGetCompanyOptions());
  const companyName = company.data?.name?.trim() || null;

  // ★ The PAGE-OWNED charge series (one per page session): the idempotency key and the request it was
  // minted for. It lives here — above the dialog, which unmounts on close — so a maybe-charged gift's
  // attempt-series survives close/reopen: re-entering the identical target+amount+funding resends
  // the SAME request under the SAME key (the backend replays — no double charge), while any edit
  // mints fresh by construction (it keys by the full gift signature, CAM-R59b). The dialog clears it
  // on confirmed success / a definite no-charge.
  const giveSeries = useRef(createGiveSeries());

  // The gift on the form is the one whose request is held (in doubt after a maybe-charged close).
  const heldGift =
    selectedTarget !== null &&
    amount !== null &&
    funding !== null &&
    giveSeries.current.heldChoiceFor({
      targetType: selectedTarget.type,
      targetId: selectedTarget.id,
      amount,
      funding,
    }) !== null;

  // Insufficient-funds PRE-SURFACE (wallet only, when the balance is known): the backend 409 is the
  // authoritative guard, but we disable Review + explain up front when we can prove insufficiency.
  // Never for the held gift: its attempt in doubt may already have debited the wallet, so the
  // balance read since can sit below it, and re-entering it is the identical resend that settles
  // it (the dialog's invariant 1, CAM-R59b; CAM-R80).
  const walletInsufficient =
    funding?.kind === 'wallet' &&
    amount !== null &&
    !heldGift &&
    !walletCoversAmount(wallet.availableCents, amount);

  const canReview =
    selectedTarget !== null && amount !== null && funding !== null && !walletInsufficient;

  // Charge confirmed (fired once, from the dialog): refresh the money reads.
  const invalidateMoneyReads = () => {
    void queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() });
    void queryClient.invalidateQueries({ queryKey: companiesGetCompanyDashboardQueryKey() });
  };

  // Every close resets the form (fresh flow next time) — WITHOUT any success signal. The success
  // toast fires ONLY via onCloseAfterSuccess below; a plain Cancel / error close must never claim
  // "Donation sent" (that false-success was review finding 2).
  const handleDialogOpenChange = (open: boolean) => {
    setConfirmOpen(open);
    if (!open) {
      setSelectedTarget(null);
      setAmountRaw('');
    }
  };

  return (
    <div
      className="mx-auto flex w-full max-w-[820px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-give')}
    >
      <PageHeader title="Donate" subtitle="Make a donation from your company's funds." />

      {/* ── Step 1: target ── */}
      <Card>
        <CardHeader
          title="Who are you donating to?"
          subtitle="Pick a charity, fund, or fundraiser."
        />
        <CardBody className="flex flex-col gap-16">
          {/* The SaaS tabs its sibling pages use (Gift Cards, Employees): the legacy Flutter-port
              strip (35px padding, 20px text) ran its third tab off a 390px phone — a live layout
              defect in the portal, fixed at the Donations mount (EP-MNT4). Test ids unchanged. */}
          <Tabs.Root
            appearance="saas"
            value={targetType}
            onValueChange={(v) => {
              setTargetType(v as TargetType);
              setSelectedTarget(null);
            }}
          >
            <Tabs.List>
              {TARGET_TABS.map((t) => (
                <Tabs.Tab key={t.type} value={t.type} testId={`give-target-tab-${t.type}`}>
                  {t.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs.Root>

          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            placeholder="Search by name…"
            aria-label="Search donation targets"
            testId="give-target-search"
          />

          {selectedTarget !== null && (
            <div
              className="flex items-center gap-10 rounded-lg border border-brand-500 bg-surface-sunken p-12"
              {...testIdProp('give-selected-target')}
            >
              <Avatar
                src={selectedTarget.logoUrl ?? undefined}
                name={selectedTarget.name}
                size="sm"
              />
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-w600 text-text-strong">{selectedTarget.name}</span>
                {selectedTarget.subtitle !== null && (
                  <span className="truncate text-s12 text-text-tertiary">
                    {selectedTarget.subtitle}
                  </span>
                )}
              </div>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                className="ml-auto"
                onClick={() => setSelectedTarget(null)}
                testId="give-selected-clear"
              >
                Change
              </Button>
            </div>
          )}

          {selectedTarget === null && (
            <TargetResults
              loading={active.isPending && active.fetchStatus !== 'idle'}
              error={active.isError}
              results={results}
              onSelect={setSelectedTarget}
              onRetry={() => void active.refetch()}
            />
          )}
        </CardBody>
      </Card>

      {/* ── Step 2: amount ── */}
      <Card>
        <CardHeader title="How much?" subtitle="Whole dollars, from your company's funds." />
        <CardBody>
          <TextField
            label="Amount (USD)"
            inputMode="numeric"
            placeholder="0"
            value={amountRaw}
            onChange={(e) => setAmountRaw(e.target.value)}
            error={amountError}
            errorMessage={amountError ? 'Enter a whole-dollar amount greater than $0.' : undefined}
            className="max-w-[220px]"
            testId="give-amount-input"
          />
        </CardBody>
      </Card>

      {/* ── Step 3: funding ── */}
      <Card>
        <CardHeader
          title="Pay with"
          subtitle="Charge a company card or debit the company wallet."
        />
        <CardBody className="flex flex-col gap-12">
          {cardsQuery.isPending || walletQuery.isPending ? (
            <Skeleton className="h-[40px] w-full max-w-[360px] rounded-lg" />
          ) : (
            <Select
              label="Funding source"
              options={fundingOptions}
              value={fundingValue}
              onValueChange={setFundingValue}
              className="max-w-[360px]"
              testId="give-payment-select"
            />
          )}

          {walletInsufficient && (
            <div
              className="flex items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12"
              {...testIdProp('give-wallet-insufficient')}
            >
              <Icon icon={WalletIcon} size={16} aria-hidden className="mt-1 text-danger-700" />
              <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                Your company wallet balance ({wallet.display}) doesn&rsquo;t cover{' '}
                {amount !== null ? formatWholeDollarUsd(amount) : 'this amount'}. Add funds or
                choose a company card.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Review → the money-moving confirm dialog ── */}
      <div className="flex items-center justify-end">
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          disabled={!canReview}
          onClick={() => setConfirmOpen(true)}
          testId="give-review-submit"
        >
          {amount !== null ? `Review — donate ${formatWholeDollarUsd(amount)}` : 'Review donation'}
        </Button>
      </div>

      {/* ── The ledger: what the company already gave (EP-WEB14). READ-ONLY — no action belongs on
          money that already moved. Always rendered, in whichever of its four states applies. ── */}
      <GiveHistoryTable
        rows={historyRows}
        loading={history.isPending}
        isError={history.isError}
        error={history.error}
        onRetry={() => void history.refetch()}
      />

      {selectedTarget !== null && amount !== null && funding !== null && (
        <DirectGiveDialog
          open={confirmOpen}
          onOpenChange={handleDialogOpenChange}
          target={selectedTarget}
          amount={amount}
          funding={funding}
          fundingLabel={fundingLabel}
          fundingSummary={fundingSummaryLabel(funding, cards, wallet)}
          companyName={companyName}
          walletAvailableCents={wallet.availableCents}
          series={giveSeries.current}
          onSuccess={invalidateMoneyReads}
          // Success-close ONLY: the toast (the reset itself happens in handleDialogOpenChange).
          onCloseAfterSuccess={() => toast.success('Donation sent')}
          // Non-success attempt close (fatal / acknowledged maybe-charged): refresh the reads the
          // user is told to check — NO success signal.
          onCloseAfterAttempt={invalidateMoneyReads}
        />
      )}
    </div>
  );
}

// ── Target result list ─────────────────────────────────────────────────────────────────────────

function TargetResults({
  loading,
  error,
  results,
  onSelect,
  onRetry,
}: {
  loading: boolean;
  error: boolean;
  results: GiveTarget[];
  onSelect: (target: GiveTarget) => void;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <ul className="flex flex-col gap-8" {...testIdProp('give-target-results')}>
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="flex items-center gap-10 rounded-lg border border-border-subtle p-12"
          >
            <Skeleton className="size-32 shrink-0 rounded-full" />
            <Skeleton className="h-12 flex-1" />
          </li>
        ))}
      </ul>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-8 py-16 text-center">
        <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
          Couldn&rsquo;t load results.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon={HeartOff} size={20} aria-hidden />}
        title="No matches"
        description="Try a different search."
      />
    );
  }
  return (
    <ul className="flex flex-col gap-8" {...testIdProp('give-target-results')}>
      {results.map((t) => (
        <li key={`${t.type}:${t.id}`}>
          <button
            type="button"
            onClick={() => onSelect(t)}
            className="flex w-full items-center gap-10 rounded-lg border border-border-subtle p-12 text-left outline-none transition-colors hover:bg-surface-hover focus-visible:border-brand-500"
            {...testIdProp(`give-target-result-${t.id}`)}
          >
            <Avatar src={t.logoUrl ?? undefined} name={t.name} size="sm" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-w600 text-text-strong">{t.name}</span>
              {t.subtitle !== null && (
                <span className="truncate text-s12 text-text-tertiary">{t.subtitle}</span>
              )}
            </div>
            <Icon
              icon={HeartHandshake}
              size={16}
              aria-hidden
              className="ml-auto text-text-tertiary"
            />
          </button>
        </li>
      ))}
    </ul>
  );
}
